import {
  joinWhales, noRowBook, ProtocolError, sniperRowBook,
  type ArbitrageDelta, type BoardDelta, type CatalogDelta, type DfsDelta, type DiscrepanciesDelta, type InplayEventDelta, type InplayListDelta, type LadderDelta, type LowHoldDelta, type PropsDelta, type ServerMessage, type SniperDelta, type TopicDeltaPayload, type TopicPayload, type RaceDelta,
  type WhaleDelta, type WhaleRowDelta,
} from "./protocol";
import {
  ladderLineId,
  type Arbitrage, type Board, type BoardView, type Catalog, type Dfs, type Discrepancies, type Entity, type EventHeader, type EventId, type EventRowsView, type Ladder, type LadderLine, type LadderLineId,
  type EndReason, type FamilyId, type InplayCell, type InplayEvent, type InplayGame, type InplayList, type LeagueId, type LeagueSummary, type LowHold, type Props, type RowId, type RowRef, type Sniper, type TopicState, type Race, type RaceList,
  type WhaleDetail, type WhaleList, type WhaleRow, type BookId, type CellKey, type WhalesCell, type PropsRow,
} from "./types";

export function emptyTopicState<V>(): TopicState<V> {
  return { phase: "loading", seq: 0, value: null, ended: null, sourceAt: null };
}

type TopicMessage = Extract<ServerMessage, { type: "snap" | "delta" }>;

export type ResyncReason = "from_mismatch" | "gap" | "protocol_error";
export type ResyncCause = { readonly reason: ResyncReason; readonly held: number; readonly seq: number; readonly from: number | null };

export type Reduction<V> =
  | { readonly kind: "applied"; readonly state: TopicState<V> }
  | { readonly kind: "ignored" }
  | { readonly kind: "resync"; readonly state: TopicState<V>; readonly cause: ResyncCause };

function resync<V>(state: TopicState<V>, msg: TopicMessage, reason: ResyncReason): Reduction<V> {
  return { kind: "resync", state: { ...state, phase: "resyncing" }, cause: { reason, held: state.seq, seq: msg.seq, from: msg.type === "delta" ? msg.from : null } };
}

function fromReason(msg: Extract<TopicMessage, { type: "delta" }>): ResyncReason {
  return msg.seq - msg.from > 1 ? "from_mismatch" : "gap";
}

export function reduceTopic<V>(
  state: TopicState<V>,
  msg: Extract<ServerMessage, { type: "snap" | "delta" }>,
  applySnap: (payload: TopicPayload, previous: V | null) => V,
  applyDelta: (value: V, payload: TopicDeltaPayload) => V,
): Reduction<V> {
  if (msg.type === "snap") {
    return { kind: "applied", state: { phase: "live", seq: msg.seq, value: applySnap(msg.payload, state.value), ended: null, sourceAt: msg.srcMs } };
  }
  if (state.phase !== "live" || state.value === null || msg.seq <= state.seq) return { kind: "ignored" };
  if (msg.from !== state.seq) return resync(state, msg, fromReason(msg));
  try {
    return {
      kind: "applied",
      state: { ...state, seq: msg.seq, value: applyDelta(state.value, msg.payload), sourceAt: msg.srcMs },
    };
  } catch (error) {
    if (error instanceof ProtocolError) return resync(state, msg, "protocol_error");
    throw error;
  }
}

type EventRow = { readonly id: RowId; readonly eventId: EventId };
export type EventRows<R extends EventRow> = { readonly events: ReadonlyMap<EventId, EventHeader>; readonly rows: ReadonlyMap<RowId, R> };
export type EventRowsDelta<R extends EventRow> = {
  readonly events: readonly EventHeader[];
  readonly rows: readonly R[];
  readonly dropRows: readonly RowId[];
  readonly dropEvents: readonly EventId[];
};

function applyEventRowsDelta<R extends EventRow>(
  value: EventRows<R>,
  delta: EventRowsDelta<R>,
  kind: string,
  orphans: "reject" | "drop",
): EventRows<R> {
  const touchesRows = delta.dropRows.length > 0 || delta.rows.length > 0 || (orphans === "drop" && delta.dropEvents.length > 0);
  const rows: Map<RowId, R> | null = touchesRows ? new Map(value.rows) : null;
  const events: Map<EventId, EventHeader> | null = delta.dropEvents.length > 0 || delta.events.length > 0 ? new Map(value.events) : null;
  if (rows === null && events === null) return value;
  for (const id of delta.dropRows) rows?.delete(id);
  for (const id of delta.dropEvents) events?.delete(id);
  for (const event of delta.events) events?.set(event.id, event);
  for (const row of delta.rows) rows?.set(row.id, row);
  const next: EventRows<R> = { events: events ?? value.events, rows: rows ?? value.rows };
  for (const row of delta.rows) {
    if (!next.events.has(row.eventId)) throw new ProtocolError(`${kind} row ${row.id} has no event`);
  }
  if (delta.dropEvents.length > 0) {
    for (const row of next.rows.values()) {
      if (next.events.has(row.eventId)) continue;
      if (orphans === "reject") throw new ProtocolError(`${kind} event ${row.eventId} dropped before its rows`);
      rows?.delete(row.id);
    }
  }
  return next;
}

export function applyBoardDelta(board: Board, delta: BoardDelta): Board {
  return applyEventRowsDelta(board, delta, "Board", "reject");
}

type WhalesRows<R extends PropsRow> = EventRows<R> & { readonly whales: ReadonlyMap<CellKey, WhalesCell> };
type WhalesRowsDelta<R extends PropsRow> = EventRowsDelta<R> & { readonly whales: readonly (readonly [CellKey, WhalesCell])[]; readonly dropWhales: readonly CellKey[] };

function applyWhalesRowsDelta<R extends PropsRow>(value: WhalesRows<R>, delta: WhalesRowsDelta<R>, kind: string, bookOf: (row: R) => BookId | null): WhalesRows<R> {
  const next = applyEventRowsDelta(value, delta, kind, "drop");
  if (delta.whales.length === 0 && delta.dropWhales.length === 0 && delta.rows.length === 0) return next === value ? value : { ...next, whales: value.whales };
  let whales = value.whales;
  if (delta.whales.length > 0 || delta.dropWhales.length > 0) {
    const held = new Map(whales);
    for (const key of delta.dropWhales) held.delete(key);
    for (const [key, summary] of delta.whales) held.set(key, summary);
    whales = held;
  }
  const keys = new Set<CellKey>(delta.dropWhales);
  for (const [key] of delta.whales) keys.add(key);
  for (const row of delta.rows) if (row.cell !== null) keys.add(row.cell);
  return { events: next.events, rows: joinWhales(next.rows, whales, keys, bookOf), whales };
}

export function applyPropsDelta(props: Props, delta: PropsDelta): Props {
  return applyWhalesRowsDelta(props, delta, "Props", noRowBook);
}

export function applyDiscrepanciesDelta(value: Discrepancies, delta: DiscrepanciesDelta): Discrepancies {
  return applyEventRowsDelta(value, delta, "Discrepancies", "drop");
}

export function applyArbitrageDelta(value: Arbitrage, delta: ArbitrageDelta): Arbitrage {
  return applyEventRowsDelta(value, delta, "Arbitrage", "drop");
}

export function applySniperDelta(value: Sniper, delta: SniperDelta): Sniper {
  return applyWhalesRowsDelta(value, delta, "Sniper", sniperRowBook);
}

export function applyLowHoldDelta(value: LowHold, delta: LowHoldDelta): LowHold {
  return applyEventRowsDelta(value, delta, "LowHold", "drop");
}

export function applyRaceDelta(value: RaceList, delta: RaceDelta): RaceList {
  if (delta.races.length === 0 && delta.drop.length === 0) return value;
  const races = new Map<string, Race>(value.races);
  for (const id of delta.drop) races.delete(id);
  for (const race of delta.races) races.set(race.id, race);
  return { races };
}

const GAME_LINES: FamilyId = "game_lines";

function placed<G extends Omit<InplayGame, "status">>(game: G, held: InplayGame | undefined): G {
  const keep = held?.gameFamilies.includes(GAME_LINES) === true && !game.gameFamilies.includes(GAME_LINES);
  return keep ? { ...game, gameFamilies: [GAME_LINES, ...game.gameFamilies] } : game;
}

export function applyInplayListSnap(previous: InplayList | null, snap: InplayList): InplayList {
  if (previous === null) return snap;
  const ended = new Set([...previous.ended, ...previous.games.keys()].filter((id) => !snap.games.has(id)));
  return { games: new Map([...snap.games].map(([id, game]) => [id, placed(game, previous.games.get(id))])), ended };
}

export function applyInplayListDelta(list: InplayList, delta: InplayListDelta): InplayList {
  if (delta.games.length === 0 && delta.statuses.length === 0 && delta.dropGames.length === 0) return list;
  const games = new Map<EventId, InplayGame>(list.games);
  const ended = new Set(list.ended);
  for (const id of delta.dropGames) {
    games.delete(id);
    ended.add(id);
  }
  for (const identity of delta.games) {
    ended.delete(identity.id);
    const held = games.get(identity.id);
    games.set(identity.id, { ...placed(identity, held), status: held?.status ?? null });
  }
  for (const [event, status] of delta.statuses) {
    const game = games.get(event);
    if (game === undefined) throw new ProtocolError(`Live status for an event that is not held: ${event}`);
    games.set(event, { ...game, status });
  }
  return { games, ended };
}

export function applyInplayEventDelta(event: InplayEvent, delta: InplayEventDelta): InplayEvent {
  if (delta.prices.length === 0 && delta.add.length === 0 && delta.drop.length === 0) return event;
  const cells = new Map<string, InplayCell>(event.cells);
  for (const id of delta.drop) cells.delete(id);
  for (const cell of delta.add) cells.set(cell.id, cell);
  for (const price of delta.prices) {
    const held = cells.get(price.id);
    if (held === undefined) throw new ProtocolError(`Live price for a cell that is not held: ${price.id}`);
    cells.set(price.id, { ...held, odds: price.odds, suspended: price.suspended, stale: price.stale });
  }
  return { cells };
}

export function applyLadderDelta(ladder: Ladder, delta: LadderDelta): Ladder {
  const lines: Map<LadderLineId, LadderLine> | null = delta.dropLines.length > 0 || delta.lines.length > 0 ? new Map(ladder.lines) : null;
  for (const [slot, line] of delta.dropLines) lines?.delete(ladderLineId(slot, line));
  for (const line of delta.lines) lines?.set(ladderLineId(line.slot, line.line), line);
  let mains = ladder.mains;
  if (delta.mains.length > 0) {
    const next = [...ladder.mains];
    for (const [slot, main] of delta.mains) {
      while (next.length < slot) next.push(null);
      next[slot] = main;
    }
    mains = next;
  }
  return lines === null && mains === ladder.mains ? ladder : { mains, lines: lines ?? ladder.lines };
}

export function applyWhaleDeltas(list: WhaleList, deltas: readonly WhaleDelta[]): WhaleList {
  let state = list.state;
  let missing = list.missing;
  let cutLowestEdge = list.cutLowestEdge;
  let rows: Map<RowId, WhaleRow> | null = null;
  for (const delta of deltas) {
    state = delta.state ?? state;
    if (delta.missing !== null && (delta.missing.length !== missing.length || delta.missing.some((league, index) => league !== missing[index]))) {
      missing = delta.missing;
    }
    cutLowestEdge = delta.cutLowestEdge ?? cutLowestEdge;
    if (delta.rows.length === 0 && delta.dropRows.length === 0) continue;
    rows ??= new Map(list.rows);
    for (const id of delta.dropRows) rows.delete(id);
    for (const row of delta.rows) rows.set(row.market_data.id, row);
  }
  if (rows === null && state === list.state && missing === list.missing && cutLowestEdge === list.cutLowestEdge) return list;
  return { state, missing, cutLowestEdge, rows: rows ?? list.rows };
}

export function reduceWhaleMessages(state: TopicState<WhaleList>, messages: readonly TopicMessage[]): Reduction<WhaleList> {
  let current = state;
  let applied = false;
  let pending: WhaleDelta[] = [];
  let seq = state.seq;
  let sourceAt = state.sourceAt;
  const settle = () => {
    if (pending.length === 0 || current.value === null) return;
    current = { ...current, seq, sourceAt, value: applyWhaleDeltas(current.value, pending) };
    pending = [];
  };
  for (const message of messages) {
    if (message.type === "snap") {
      if (message.payload.kind !== "whale") return resync(current, message, "protocol_error");
      pending = [];
      current = { phase: "live", seq: message.seq, value: message.payload.snap, ended: null, sourceAt: message.srcMs };
      seq = message.seq;
      sourceAt = message.srcMs;
      applied = true;
      continue;
    }
    if (current.phase !== "live" || current.value === null || message.seq <= seq) continue;
    if (message.from !== seq || message.payload.kind !== "whale") {
      settle();
      return resync(current, message, message.payload.kind === "whale" ? fromReason(message) : "protocol_error");
    }
    pending.push(message.payload.delta);
    seq = message.seq;
    sourceAt = message.srcMs;
    applied = true;
  }
  settle();
  return applied ? { kind: "applied", state: current } : { kind: "ignored" };
}

export function applyWhaleRowDelta(detail: WhaleDetail, delta: WhaleRowDelta): WhaleDetail {
  const row = delta.row ?? detail.row;
  const state = delta.state ?? detail.state;
  return row === detail.row && state === detail.state ? detail : { state, row };
}

export function applyCatalogDelta(catalog: Catalog, delta: CatalogDelta): Catalog {
  if (delta.leagues.length === 0) return catalog;
  const leagues = new Map<LeagueId, LeagueSummary>(catalog.leagues);
  const added: LeagueId[] = [];
  for (const league of delta.leagues) {
    if (!leagues.has(league.id)) added.push(league.id);
    leagues.set(league.id, league);
  }
  return { books: catalog.books, leagues, leagueOrder: added.length > 0 ? [...catalog.leagueOrder, ...added] : catalog.leagueOrder };
}

export function applyDfsDelta(dfs: Dfs, delta: DfsDelta): Dfs {
  if (delta.leagues.length === 0) return dfs;
  const next = new Map(dfs);
  for (const [id, league] of delta.leagues) {
    if (league === null) next.delete(id);
    else next.set(id, league);
  }
  return next;
}

const noEvents: ReadonlyMap<EventId, EventHeader> = new Map();
const noRows: ReadonlyMap<RowId, never> = new Map<RowId, never>();
const nameOrder = new Intl.Collator("en", { sensitivity: "base", numeric: true });

type SortableRow = { readonly eventId: EventId; readonly entity: Entity | null };

export function compareEntities(a: Entity | null, b: Entity | null): number {
  const byOrder = (a?.order ?? 0) - (b?.order ?? 0);
  if (byOrder !== 0) return byOrder;
  const byName = nameOrder.compare(a?.name ?? "", b?.name ?? "");
  if (byName !== 0) return byName;
  const keyA = a?.key ?? "";
  const keyB = b?.key ?? "";
  return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
}

function sortRowIds(rows: ReadonlyMap<RowId, SortableRow>, events: ReadonlyMap<EventId, EventHeader>): RowId[] {
  return [...rows.keys()].sort((left, right) => {
    const a = rows.get(left);
    const b = rows.get(right);
    const startA = a ? events.get(a.eventId)?.startMs ?? Infinity : Infinity;
    const startB = b ? events.get(b.eventId)?.startMs ?? Infinity : Infinity;
    if (startA !== startB) return startA < startB ? -1 : 1;
    const eventA = a?.eventId ?? "";
    const eventB = b?.eventId ?? "";
    if (eventA !== eventB) return eventA < eventB ? -1 : 1;
    const byEntity = compareEntities(a?.entity ?? null, b?.entity ?? null);
    if (byEntity !== 0) return byEntity;
    return left < right ? -1 : left > right ? 1 : 0;
  });
}

function sameMembership(refs: ReadonlyMap<RowId, RowRef>, board: Board | null): boolean {
  if (board === null) return refs.size === 0;
  if (refs.size !== board.rows.size) return false;
  for (const row of board.rows.values()) {
    const ref = refs.get(row.id);
    if (ref === undefined || ref.eventId !== row.eventId || ref.entity?.key !== row.entity?.key || ref.entity?.name !== row.entity?.name || ref.entity?.order !== row.entity?.order) {
      return false;
    }
  }
  return true;
}

export function boardView(previous: BoardView | null, state: TopicState<Board>): BoardView {
  const board = state.value;
  const events = board?.events ?? noEvents;
  if (previous !== null && previous.events === events && sameMembership(previous.rows, board)) {
    return previous.phase === state.phase && previous.ended === state.ended
      ? previous
      : { ...previous, phase: state.phase, ended: state.ended };
  }
  const rows = new Map<RowId, RowRef>();
  for (const row of board?.rows.values() ?? []) rows.set(row.id, { eventId: row.eventId, entity: row.entity });
  return { phase: state.phase, ended: state.ended, events, rows, rowIds: sortRowIds(rows, events) };
}

export type ViewRow = EventRow & { readonly entity: Entity };

function sameRowSet<R extends ViewRow>(previous: ReadonlyMap<RowId, R>, next: ReadonlyMap<RowId, R>): boolean {
  if (previous.size !== next.size) return false;
  for (const id of next.keys()) if (!sameRowAt(previous, next, id)) return false;
  return true;
}

function sameRowAt<R extends ViewRow>(previous: ReadonlyMap<RowId, R>, next: ReadonlyMap<RowId, R>, id: RowId): boolean {
  const held = previous.get(id);
  const row = next.get(id);
  return held === row || (held !== undefined && row !== undefined && held.eventId === row.eventId && held.entity.name === row.entity.name);
}

function keepsRowSet<R extends ViewRow>(previous: ReadonlyMap<RowId, R>, next: ReadonlyMap<RowId, R>, delta: EventRowsDelta<R> | null): boolean {
  if (delta === null) return sameRowSet(previous, next);
  return delta.dropRows.every((id) => sameRowAt(previous, next, id)) && delta.rows.every((row) => sameRowAt(previous, next, row.id));
}

export function propsView<R extends ViewRow>(previous: EventRowsView<R> | null, state: TopicState<EventRows<R>>, delta: EventRowsDelta<R> | null = null): EventRowsView<R> {
  const props = state.value;
  const events = props?.events ?? noEvents;
  const rows: ReadonlyMap<RowId, R> = props?.rows ?? noRows;
  const pending = state.phase === "loading" ? 1 : 0;
  if (previous !== null && previous.events === events && previous.rows === rows) {
    return previous.phase === state.phase && previous.ended === state.ended
      ? previous
      : { ...previous, phase: state.phase, pending, ended: state.ended };
  }
  const rowIds = previous !== null && previous.events === events && keepsRowSet(previous.rows, rows, delta)
    ? previous.rowIds
    : sortRowIds(rows, events);
  return { phase: state.phase, pending, ended: state.ended, events, rows, rowIds };
}

const EMPTY_ROWS_VIEW: EventRowsView<never> = { phase: "loading", pending: 0, ended: null, events: noEvents, rows: noRows, rowIds: [] };

export function mergePropsViews<R extends ViewRow>(views: readonly EventRowsView<R>[]): EventRowsView<R> {
  if (views.length === 1) return views[0];
  if (views.length === 0) return EMPTY_ROWS_VIEW;
  const events = new Map<EventId, EventHeader>();
  const rows = new Map<RowId, R>();
  const rowIds: RowId[] = [];
  let live = false;
  let resyncing = false;
  let allEnded = true;
  let pending = 0;
  let ended: EndReason | null = null;
  for (const view of views) {
    if (view.phase === "live") live = true;
    if (view.phase === "loading") pending += 1;
    if (view.phase === "resyncing") resyncing = true;
    if (view.ended === null) allEnded = false;
    else ended ??= view.ended;
    for (const [id, event] of view.events) events.set(id, event);
    for (const [id, row] of view.rows) rows.set(id, row);
    for (const id of view.rowIds) rowIds.push(id);
  }
  return {
    phase: live ? "live" : resyncing ? "resyncing" : allEnded ? "ended" : "loading",
    pending,
    ended: allEnded ? ended : null,
    events,
    rows,
    rowIds,
  };
}
